import {
    createApiRef,
    DiscoveryApi,
    IdentityApi,
} from '@backstage/core-plugin-api';
import type { CompoundEntityRef } from '@backstage/catalog-model';
import { ResponseError } from '@backstage/errors';
import {
    ContainerLog,
    ForensicsItemDetail,
    ForensicsItemSummary
} from './types';

export const forensicsApiRef = createApiRef<ForensicsApi>({
    id: 'plugin.otk-forensics.service',
});

export interface ForensicsApi {
    /**
     * List all forensics items for given otk service entity
     */
    listForensicsItems(options: {
        teamspace: string;
        app: string;
        type: string;
    }): Promise<ForensicsItemSummary[]>;

    /**
     * Get a single forensics item.
     */
    getForensicsItemDetail(options: {
        prefix: string;
    }): Promise<ForensicsItemDetail>;

    /**
     * Get specific container log.
     */
    getContainerLog(options: {
        prefix: string;
        pod: string;
        container: string;
    }): Promise<ContainerLog>;
}

export class ForensicsClient implements ForensicsApi {
    private readonly discoveryApi: DiscoveryApi;
    private readonly identityApi: IdentityApi;

    constructor(options: {
        discoveryApi: DiscoveryApi;
        identityApi: IdentityApi;
    }) {
        this.discoveryApi = options.discoveryApi;
        this.identityApi = options.identityApi;
    }

    async listForensicsItems(options: {
        teamspace: string;
        app: string;
        type: string;
    }): Promise<ForensicsItemSummary[]> {
        const { teamspace, app, type } = options;

        const url = new URL(
            `${await this.discoveryApi.getBaseUrl(
              'forensics',
            )}/${type}/${encodeURIComponent(teamspace)}/${encodeURIComponent(
                app,
            )}.json`,
        );

        const idToken = await this.getToken();
        const response = await fetch(url.href, {
            method: 'GET',
            headers: {
                ...(idToken && { Authorization: `Bearer ${idToken}` }),
            },
        });

        return (
            (await response.json()) || []
        );
    }

    async getForensicsItemDetail(options: {
        prefix: string;
    }): Promise<ForensicsItemDetail> {
        const { prefix } = options;
        const url = `${await this.discoveryApi.getBaseUrl(
          'forensics',
        )}/forensics.json?prefix=${encodeURIComponent(prefix)}`;

        const idToken = await this.getToken();
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                ...(idToken && { Authorization: `Bearer ${idToken}` }),
            },
        });

        return (await response.json());
    }

    async getContainerLog(options: { prefix: string; pod: string; container: string; }): Promise<ContainerLog> {
        const { prefix, pod, container } = options;
        const url = `${await this.discoveryApi.getBaseUrl(
            'forensics',
        )}/container-logs.json?prefix=${encodeURIComponent(prefix)}&pod=${encodeURIComponent(pod)}&container=${encodeURIComponent(container)}`;

        const idToken = await this.getToken();
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                ...(idToken && { Authorization: `Bearer ${idToken}` }),
            },
        });

        return (await response.json());
    }

    private async getToken() {
        const { token } = await this.identityApi.getCredentials();
        return token;
    }
}